import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ApplicationRef,
  ErrorHandler,
  Inject,
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import type {
  UpdateActivatedEvent,
  UpdateAvailableEvent,
} from '@angular/service-worker';
import { SwUpdate } from '@angular/service-worker';
import type { AppInfo } from '@capacitor/app';
import { App } from '@capacitor/app';
// T244825 Remove deprecated import.
// eslint-disable-next-line import/no-deprecated
import type { PluginRegistry } from '@capacitor/core';
import { Capacitor, Plugins } from '@capacitor/core';
import type { DeviceId, DeviceInfo } from '@capacitor/device';
import { Device } from '@capacitor/device';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type { FreelancerLocationPwaInterface } from '@freelancer/location';
import { Location } from '@freelancer/location';
import { TimeUtils } from '@freelancer/time-utils';
import { firstValueFrom, toNumber } from '@freelancer/utils';
import compareVersions from 'compare-versions';
import { CookieService } from 'ngx-cookie';
import * as Rx from 'rxjs';
import {
  shareReplay,
  catchError,
  filter,
  first,
  map,
  skip,
  switchMap,
} from 'rxjs/operators';
import type {
  AppVersion,
  NativeDeviceInfo,
  NgswDebugInfo,
  Platform,
} from './pwa.model';

/**
 * Custom AppData from the Ngsw config included in the update notifications.
 */
export interface CustomAppData {
  /* Git SHA of the version */
  gitRevision: string;
  /* Build time as a Unix timestamp of the version */
  buildTimestamp: number;
  /* Minimum version build time to force the upgrade to */
  minimumVersionTimestamp: number;
}

export interface NativeAppVersion {
  major: number;
  minor: number;
}

/**
 * Wraps Angular's SwUpdate service & provide additional PWA-related helpers
 */
@Injectable({
  providedIn: 'root',
})
export class Pwa implements FreelancerLocationPwaInterface {
  @RepetitiveSubscription()
  private backgroundUpdateChecksSubscription?: Rx.Subscription;
  private isInstalledModeActivated = false;
  private appVersion$: Rx.Observable<AppVersion | undefined>;
  private nativeDeviceInfo$: Rx.Observable<NativeDeviceInfo>;
  private launchUrl?: string;

  constructor(
    private appRef: ApplicationRef,
    private cookies: CookieService,
    private location: Location,
    private rendererFactory: RendererFactory2,
    private router: Router,
    private timeUtils: TimeUtils,
    private updates: SwUpdate,
    private http: HttpClient,
    private errorHandler: ErrorHandler,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Detects if the PWA is running in installed mode, i.e. launched as a
   * standalone app.
   *
   * Use that to hide "Install app" banners & buttons for instance.
   */
  isInstalled(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      (window.navigator.standalone === true ||
        window.matchMedia('(display-mode: standalone)').matches ||
        this.isNative() ||
        this.isInstalledModeActivated)
    );
  }

  /*
   * Detects if the PWA is as a native app (through Capacitor)
   *
   * Use that to guard native only features, i.e. Capacitor plugins access
   */
  isNative(): boolean {
    // Using the old method is still required as this is used to check
    // if we're on the Capacitor 2 native app.
    // eslint-disable-next-line deprecation/deprecation
    return Capacitor.isNative === true;
  }

  /**
   * Determines if the native app is running in Capacitor 2 or 3.
   *
   * FIXME: Remove once we've forced upgrades on everyone
   *
   * @deprecated
   */
  isCapacitor2(): boolean {
    // These have been removed as of Capacitor 3
    return 'isAndroid' in Capacitor || 'isIOS' in Capacitor;
  }

  /**
   * Returns the platform the PWA is running as.
   *
   * Use this for specific functionality to comply with requirements on certain app platforms.
   */
  getPlatform(): Platform {
    const platform = Capacitor.getPlatform();
    if (platform === 'web' || platform === 'ios' || platform === 'android') {
      return platform;
    }
    return 'unknown';
  }

  /**
   * Promise of the Capacitor Plugins object allowing the PWA to use native
   * APIs.
   *
   * @deprecated
   */
  // eslint-disable-next-line deprecation/deprecation
  capacitorPlugins(): Promise<PluginRegistry> {
    // eslint-disable-next-line deprecation/deprecation
    return Promise.resolve(Plugins);
  }

  /**
   * Returns the app version info
   */
  appVersion(): Rx.Observable<AppVersion | undefined> {
    // This handles non webapp pages where window.webapp is not going to be defined
    if (!window.webapp) {
      return Rx.of(undefined);
    }
    const gitRevision = window.webapp.version.gitRevision.substring(0, 7);
    const buildTimestampInSeconds = Math.round(
      window.webapp.version.buildTimestamp / 1000,
    );

    // App.getInfo() is currently not available for the web
    // Ref: https://github.com/ionic-team/capacitor-plugins/pull/71#discussion_r507911090
    if (!this.isNative()) {
      return Rx.of({ gitRevision, buildTimestampInSeconds });
    }

    // Setup a hot observable to cache data from Capacitor API.
    if (!this.appVersion$) {
      this.appVersion$ = this.getCapacitorAppInfo().pipe(
        map(appInfo => {
          return {
            gitRevision,
            buildTimestampInSeconds,
            nativeVersion: appInfo.version,
            nativeBuildTimestampInSeconds:
              this.getPlatform() === 'android'
                ? // The Android version code is messed up because people messed it up in
                  // the past (by using a ridiculously high value very close to Google
                  // Play's limit).
                  // We use a convoluted formula to calculate the version code from the
                  // timestamp in webapp/projects/main/native/android/fastlane/Fastfile,
                  // and this logic reverses it into a timestamp.
                  (toNumber(appInfo.build) - 1901538326) * 1000
                : toNumber(appInfo.build),
            nativeBuildNumber: toNumber(appInfo.build),
          };
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
    // Serve the same observable to avoid querying the Capacitor API
    // every single time unless we want fresh data by deliberately
    // setting the "refresh" parameter.
    return this.appVersion$;
  }

  /**
   * Return true is the current nativeVersion is equal or greater the required version
   */
  async hasMinimumNativeVersion(version: NativeAppVersion): Promise<boolean> {
    if (!this.isNative()) {
      return false;
    }

    // Check if nativeVersion is defined
    const appVersion = await firstValueFrom(this.appVersion());

    if (!appVersion || !appVersion.nativeVersion) {
      this.errorHandler.handleError(new Error('nativeVersion is not defined'));
      return false;
    }

    const { nativeVersion: currentVersion } = appVersion;
    const requiredVersion = `${version.major}.${version.minor}`;

    // Return true for local and dev builds which uses 0.1.0
    if (currentVersion === '0.1.0') {
      return true;
    }

    return compareVersions.compare(currentVersion, requiredVersion, '>=');
  }

  /**
   * Returns the native device info, or undefined on non-native
   */
  nativeDeviceInfo(): Rx.Observable<NativeDeviceInfo | undefined> {
    if (!this.isNative()) {
      return Rx.of(undefined);
    }

    if (!this.nativeDeviceInfo$) {
      this.nativeDeviceInfo$ = Rx.combineLatest([
        // Need to get stream from BOTH DeviceInfo AND DeviceId as Capacitor 3
        // no longer provides UUID as part of DeviceInfo.
        this.getCapacitorDeviceInfo(),
        this.getCapacitorDeviceId(),
      ]).pipe(
        map(([info, { uuid }]) => {
          return {
            name: info.name,
            model: info.model,
            uuid,
            osVersion: info.osVersion,
            manufacturer: info.manufacturer,
            isVirtual: info.isVirtual,
            memUsed: info.memUsed,
          };
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this.nativeDeviceInfo$;
  }

  /**
   * @private
   * Activate the PWA installed mode, i.e. tweak a bit the global styles &
   * behaviors
   *
   * This is turned on when the PWA is running in installed mode, or when it is
   * loaded from the `/internal/pwa` route for easier testing.
   */
  activateInstalledMode(): void {
    if (!this.isInstalledModeActivated) {
      // Disable text selection & browser callouts as it's not "app-like"
      const renderer = this.rendererFactory.createRenderer(null, null);
      renderer.setStyle(this.document.body, '-webkit-user-select', 'none');

      if (isPlatformBrowser(this.platformId)) {
        // Turn off the m. redirections
        this.cookies.put('mobile_optout', 'true');
      }

      this.isInstalledModeActivated = true;
    }
  }

  /**
   * @private
   * Use these events to notify the user of a pending update or to refresh
   * their pages when the code they are running is out of date.
   */
  // eslint-disable-next-line deprecation/deprecation
  hasPendingUpdate(): Rx.Observable<UpdateAvailableEvent> {
    // eslint-disable-next-line deprecation/deprecation
    return this.updates.available;
  }

  /**
   * @private
   * Use these events to notify the user that an update has been downloaded and
   * activated.
   */
  // eslint-disable-next-line deprecation/deprecation
  hasActivatedUpdate(): Rx.Observable<UpdateActivatedEvent> {
    // eslint-disable-next-line deprecation/deprecation
    return this.updates.activated;
  }

  /**
   * @private
   * Check for available updates
   */
  checkForUpdate(): Promise<boolean | void> {
    if (this.updates.isEnabled) {
      return this.updates.checkForUpdate();
    }
    return Promise.resolve();
  }

  /**
   * @private
   * Update to the latest app version immediately
   */
  activateUpdate(): Promise<void> {
    return this.updates
      .activateUpdate()
      .then(() => this.document.location.reload());
  }

  /**
   * @private
   * Update to the latest app version on the next navigation
   */
  activateUpdateOnNavigation(): Promise<void> {
    return firstValueFrom(
      this.router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
      ),
    ).then(e =>
      this.updates.activateUpdate().then(() => {
        this.document.location.href = new URL(
          e.url,
          this.location.origin,
        ).toString();
      }),
    );
  }

  /**
   * @private
   * Ask the service worker to check if any updates have been deployed to the
   * server every hour
   */
  startBackgroundUpdateChecks() {
    if (this.updates.isEnabled) {
      const appIsStable$ = this.appRef.isStable.pipe(
        first(isStable => isStable),
      );
      const everyOneHour$ = this.timeUtils.rxInterval(1 * 60 * 60 * 1000);
      const everyOneHourOnceAppIsStable$ = Rx.concat(
        appIsStable$,
        everyOneHour$,
      ).pipe(skip(1));
      this.backgroundUpdateChecksSubscription = everyOneHourOnceAppIsStable$.subscribe(
        () => {
          this.updates.checkForUpdate();
        },
      );
    }
  }

  /**
   * @private
   * Cancel the background update checks
   */
  stopBackgroundUpdateChecks() {
    if (this.backgroundUpdateChecksSubscription) {
      this.backgroundUpdateChecksSubscription.unsubscribe();
    }
  }

  /**
   * Forward debug information from `/ngsw/state`
   * which is an Angular service worker specific endpoint.
   *
   * FIXME: T236180 Hopefully the Angular service worker returns the debug info
   *        in JSON format in the future so that we don't have to parse formatted plain text.
   */
  getNgswDebugInfo(): Rx.Observable<NgswDebugInfo> {
    // Parse the string with the given regex and
    // return a JSON named capturing groups.
    const ngswStateParser = (rawString: string) => {
      const regex = new RegExp(
        [
          // To allow capturing of an error message that spans across multiple
          // lines, [^] is used.
          'Driver state: (?<driverState>[^]+)\n',
          'Latest manifest hash: (?<latestManifestHash>[^\n]+)\n',
          'Last update check: (?<lastUpdateCheck>[^\n]+)\n',
          '(.|\n)*',
          'Debug log:\n(?<debugLog>(.|\n)+)',
        ].join(''),
      );
      const groups = rawString.match(regex)?.groups ?? {};
      if (
        'driverState' in groups &&
        'latestManifestHash' in groups &&
        'lastUpdateCheck' in groups
      ) {
        return {
          driverState: groups.driverState,
          lastUpdateCheck: groups.lastUpdateCheck,
          latestManifestHash: groups.latestManifestHash,
        } as NgswDebugInfo;
      }
    };

    if (this.updates.isEnabled) {
      /**
       * Access the service worker checksum in the native Capacitor app.
       * This endpoint is only implemented in iOS for the e2e test.
       */
      const checksum$ =
        this.getPlatform() === 'ios'
          ? this.http
              .get('/sw-checksum', {
                responseType: 'text',
              })
              .pipe(catchError(_ => Rx.of(undefined)))
          : Rx.of(undefined);

      return Rx.combineLatest([
        this.http.get('/ngsw/state', {
          responseType: 'text',
        }),
        checksum$,
      ]).pipe(
        switchMap(([state, scriptChecksum]) => {
          const ngswState = ngswStateParser(state);
          if (ngswState) {
            return Rx.of({
              ...ngswState,
              scriptChecksum,
            });
          }
          return Rx.EMPTY;
        }),
      );
    }
    return Rx.EMPTY;
  }

  /**
   * Returns the app version info
   */
  getCapacitorAppInfo(): Rx.Observable<AppInfo> {
    return Rx.from(App.getInfo());
  }

  /**
   * This function gets the launch URL set by either notifications or universal
   * link.
   *
   * This should help with UX by going directly to the correct page, rather
   * than the user seeing the dashboard/login screen for a split second before
   * going to the desired page.
   *
   * @returns launch URL.
   */
  async getLaunchUrl(): Promise<string | undefined> {
    return Promise.resolve(this.launchUrl);
  }

  setLaunchUrl(launchUrl: string): void {
    this.launchUrl = launchUrl;
  }

  /**
   * Obtain the device info from the Capacitor API.
   * Cached data is served unless "refresh" is set.
   *
   * @returns An observable of Capacitor device ID.
   */
  private getCapacitorDeviceId(): Rx.Observable<DeviceId> {
    return Rx.from(Device.getId());
  }

  /**
   * Obtain the device info from the Capacitor API.
   * Cached data is served unless "refresh" is set.
   *
   * @returns An observable of Capacitor device info.
   */
  private getCapacitorDeviceInfo(): Rx.Observable<DeviceInfo> {
    return Rx.from(Device.getInfo());
  }
}
